import { type EnvironmentId, type ScopedThreadRef, type ThreadId } from "@cmmd-forge/contracts";
import { scopedThreadKey } from "@cmmd-forge/client-runtime";
import { projectScriptCwd } from "@cmmd-forge/shared/projectScripts";
import { useCallback, useEffect, useRef, useState } from "react";
import { newCommandId, randomUUID } from "~/lib/utils";
import { runThreadExecutionIntentAction } from "../../lib/runThreadExecutionIntentAction";
import { type ProjectScript, type Project, type Thread } from "../../types";
import { selectThreadTerminalState, useTerminalStateStore } from "../../terminalStateStore";
import { buildProjectScriptTerminalLaunchPlan, hasLaunchRuntimeEnv } from "../ChatView.logic";
import { type TerminalLaunchContext } from "./PersistentThreadTerminalDrawer";
import { type ServerThreadStatus } from "./serverThreadStatus";
import { closeTerminalSessionGuarded } from "./useThreadTerminalSessionControls";

/**
 * ChatView's terminal controls: launch context for new sessions, focus
 * bumping, open and split actions, and project-script launches that route
 * through the terminal store.
 */
export function useChatTerminalControls(input: {
  readonly environmentId: EnvironmentId;
  readonly routeThreadRef: ScopedThreadRef;
  readonly serverThreadStatus: ServerThreadStatus;
  readonly activeThreadId: ThreadId | null;
  readonly activeThreadRef: ScopedThreadRef | null;
  readonly activeThreadKey: string | null;
  readonly activeThread: Thread | undefined;
  readonly activeProject: Project | null;
  readonly activeProjectCwd: string | null;
  readonly activeThreadWorktreePath: string | null;
  readonly gitCwd: string | null;
  readonly terminalState: ReturnType<typeof selectThreadTerminalState>;
  readonly hasReachedSplitLimit: boolean;
  readonly focusComposer: () => void;
  readonly setThreadError: (targetThreadId: ThreadId | null, error: string | null) => void;
  readonly setLastInvokedScriptByProjectId: (
    updater: (current: Record<string, string>) => Record<string, string>,
  ) => void;
}): {
  readonly terminalFocusRequestId: number;
  readonly terminalLaunchContext: TerminalLaunchContext | null;
  readonly activeTerminalLaunchContext: TerminalLaunchContext | null;
  readonly setTerminalOpen: (open: boolean) => void;
  readonly toggleTerminalVisibility: () => void;
  readonly splitTerminal: () => void;
  readonly createNewTerminal: () => void;
  readonly closeTerminal: (terminalId: string) => void;
  readonly runProjectScript: (
    script: ProjectScript,
    options?: {
      readonly cwd?: string;
      readonly env?: Record<string, string>;
      readonly worktreePath?: string | null;
      readonly preferNewTerminal?: boolean;
      readonly rememberAsLastInvoked?: boolean;
    },
  ) => Promise<void>;
} {
  const {
    activeProject,
    activeProjectCwd,
    activeThread,
    activeThreadId,
    activeThreadKey,
    activeThreadRef,
    activeThreadWorktreePath,
    environmentId,
    focusComposer,
    gitCwd,
    hasReachedSplitLimit,
    routeThreadRef,
    serverThreadStatus,
    setLastInvokedScriptByProjectId,
    setThreadError,
    terminalState,
  } = input;
  const [terminalFocusRequestId, setTerminalFocusRequestId] = useState(0);
  const [terminalLaunchContext, setTerminalLaunchContext] = useState<TerminalLaunchContext | null>(
    null,
  );
  const terminalOpenByThreadRef = useRef<Record<string, boolean>>({});
  const storeSetTerminalOpen = useTerminalStateStore((state) => state.setTerminalOpen);
  const storeSplitTerminal = useTerminalStateStore((state) => state.splitTerminal);
  const storeNewTerminal = useTerminalStateStore((state) => state.newTerminal);
  const storeSetActiveTerminal = useTerminalStateStore((state) => state.setActiveTerminal);
  const storeCloseTerminal = useTerminalStateStore((state) => state.closeTerminal);
  const storeClearTerminalLaunchContext = useTerminalStateStore(
    (state) => state.clearTerminalLaunchContext,
  );
  const storeServerTerminalLaunchContext = useTerminalStateStore((state) =>
    activeThreadRef
      ? (state.terminalLaunchContextByThreadKey[scopedThreadKey(activeThreadRef)] ?? null)
      : null,
  );
  const activeTerminalLaunchContext =
    terminalLaunchContext?.threadId === activeThreadId
      ? terminalLaunchContext
      : activeThreadId && storeServerTerminalLaunchContext
        ? { threadId: activeThreadId, ...storeServerTerminalLaunchContext }
        : null;

  const setTerminalOpen = useCallback(
    (open: boolean) => {
      if (!activeThreadRef) return;
      storeSetTerminalOpen(activeThreadRef, open);
    },
    [activeThreadRef, storeSetTerminalOpen],
  );

  const toggleTerminalVisibility = useCallback(() => {
    if (!activeThreadRef) return;
    setTerminalOpen(!terminalState.terminalOpen);
  }, [activeThreadRef, setTerminalOpen, terminalState.terminalOpen]);

  const splitTerminal = useCallback(() => {
    if (!activeThreadRef || hasReachedSplitLimit) return;
    const terminalId = `terminal-${randomUUID()}`;
    storeSplitTerminal(activeThreadRef, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
  }, [activeThreadRef, hasReachedSplitLimit, storeSplitTerminal]);

  const createNewTerminal = useCallback(() => {
    if (!activeThreadRef) return;
    const terminalId = `terminal-${randomUUID()}`;
    storeNewTerminal(activeThreadRef, terminalId);
    setTerminalFocusRequestId((value) => value + 1);
  }, [activeThreadRef, storeNewTerminal]);

  const closeTerminal = useCallback(
    (terminalId: string) => {
      if (!activeThreadId || !activeThreadRef) return;
      const closed = closeTerminalSessionGuarded({
        threadRef: activeThreadRef,
        threadId: activeThreadId,
        terminalId,
        isFinalSession: terminalState.terminalIds.length <= 1,
      });
      if (!closed) return;
      storeCloseTerminal(activeThreadRef, terminalId);
      setTerminalFocusRequestId((value) => value + 1);
    },
    [activeThreadId, activeThreadRef, storeCloseTerminal, terminalState.terminalIds.length],
  );

  const runProjectScript = useCallback(
    async (
      script: ProjectScript,
      options?: {
        readonly cwd?: string;
        readonly env?: Record<string, string>;
        readonly worktreePath?: string | null;
        readonly preferNewTerminal?: boolean;
        readonly rememberAsLastInvoked?: boolean;
      },
    ) => {
      if (!activeThreadId || !activeProject || !activeThread) return;
      if (options?.rememberAsLastInvoked !== false) {
        setLastInvokedScriptByProjectId((current) => {
          if (current[activeProject.id] === script.id) return current;
          return { ...current, [activeProject.id]: script.id };
        });
      }
      const launchPlan = buildProjectScriptTerminalLaunchPlan({
        activeProject,
        activeThread,
        activeThreadId,
        script,
        gitCwd,
        terminalState: {
          activeTerminalId: terminalState.activeTerminalId,
          terminalIds: terminalState.terminalIds,
          runningTerminalIds: terminalState.runningTerminalIds,
        },
        randomTerminalId: () => `terminal-${randomUUID()}`,
        options,
      });

      setTerminalLaunchContext({
        threadId: activeThreadId,
        terminalId: launchPlan.targetTerminalId,
        cwd: launchPlan.targetCwd,
        worktreePath: launchPlan.targetWorktreePath,
        runtimeEnv: launchPlan.runtimeEnv,
      });
      if (!activeThreadRef) {
        return;
      }
      if (launchPlan.storeAction === "new-terminal") {
        storeNewTerminal(activeThreadRef, launchPlan.targetTerminalId, {
          openDrawer: false,
        });
      } else {
        storeSetActiveTerminal(activeThreadRef, launchPlan.targetTerminalId);
      }

      try {
        await runThreadExecutionIntentAction(
          environmentId,
          {
            action: "terminal",
            projectId: activeProject.cmmdRegistration?.cmmdProjectId ?? null,
            targetId: activeThreadId,
            dispatchIdentity: newCommandId(),
          },
          {
            dispatch: async (api) => {
              await api.terminal.open(launchPlan.openInput);
              await api.terminal.write({
                threadId: activeThreadId,
                terminalId: launchPlan.targetTerminalId,
                data: `${script.command}\r`,
              });
            },
          },
        );
      } catch (error) {
        setThreadError(
          activeThreadId,
          error instanceof Error ? error.message : `Failed to run script "${script.name}".`,
        );
      }
    },
    [
      activeProject,
      activeThread,
      activeThreadId,
      activeThreadRef,
      environmentId,
      gitCwd,
      setLastInvokedScriptByProjectId,
      setThreadError,
      storeNewTerminal,
      storeSetActiveTerminal,
      terminalState.activeTerminalId,
      terminalState.runningTerminalIds,
      terminalState.terminalIds,
    ],
  );

  useEffect(() => {
    if (!activeThreadId) {
      setTerminalLaunchContext(null);
      // Thread routes stay mounted while their shell/detail subscription catches up.
      // Preserve persisted terminal context through that gap; missing routes still
      // clear it below once the status is no longer a bootstrap state.
      if (serverThreadStatus !== "loading" && serverThreadStatus !== "hydrating") {
        storeClearTerminalLaunchContext(routeThreadRef);
      }
      return;
    }
    setTerminalLaunchContext((current) => {
      if (!current) return current;
      if (current.threadId === activeThreadId) return current;
      return null;
    });
  }, [activeThreadId, routeThreadRef, serverThreadStatus, storeClearTerminalLaunchContext]);

  useEffect(() => {
    if (!activeThreadId || !activeProjectCwd) {
      return;
    }
    setTerminalLaunchContext((current) => {
      if (!current || current.threadId !== activeThreadId) {
        return current;
      }
      const settledCwd = projectScriptCwd({
        project: { cwd: activeProjectCwd },
        worktreePath: activeThreadWorktreePath,
      });
      if (
        settledCwd === current.cwd &&
        (activeThreadWorktreePath ?? null) === current.worktreePath &&
        !hasLaunchRuntimeEnv(current)
      ) {
        if (activeThreadRef) {
          storeClearTerminalLaunchContext(activeThreadRef);
        }
        return null;
      }
      return current;
    });
  }, [
    activeProjectCwd,
    activeThreadId,
    activeThreadRef,
    activeThreadWorktreePath,
    storeClearTerminalLaunchContext,
  ]);

  useEffect(() => {
    if (!activeThreadId || !activeProjectCwd || !storeServerTerminalLaunchContext) {
      return;
    }
    const settledCwd = projectScriptCwd({
      project: { cwd: activeProjectCwd },
      worktreePath: activeThreadWorktreePath,
    });
    if (
      settledCwd === storeServerTerminalLaunchContext.cwd &&
      (activeThreadWorktreePath ?? null) === storeServerTerminalLaunchContext.worktreePath &&
      !hasLaunchRuntimeEnv(storeServerTerminalLaunchContext)
    ) {
      if (activeThreadRef) {
        storeClearTerminalLaunchContext(activeThreadRef);
      }
    }
  }, [
    activeProjectCwd,
    activeThreadId,
    activeThreadRef,
    activeThreadWorktreePath,
    storeClearTerminalLaunchContext,
    storeServerTerminalLaunchContext,
  ]);

  useEffect(() => {
    if (terminalState.terminalOpen) {
      return;
    }
    if (activeThreadRef) {
      storeClearTerminalLaunchContext(activeThreadRef);
    }
    setTerminalLaunchContext((current) => (current?.threadId === activeThreadId ? null : current));
  }, [
    activeThreadId,
    activeThreadRef,
    storeClearTerminalLaunchContext,
    terminalState.terminalOpen,
  ]);

  useEffect(() => {
    if (!activeThreadKey) return;
    const previous = terminalOpenByThreadRef.current[activeThreadKey] ?? false;
    const current = Boolean(terminalState.terminalOpen);

    if (!previous && current) {
      terminalOpenByThreadRef.current[activeThreadKey] = current;
      setTerminalFocusRequestId((value) => value + 1);
      return;
    } else if (previous && !current) {
      terminalOpenByThreadRef.current[activeThreadKey] = current;
      const frame = window.requestAnimationFrame(() => {
        focusComposer();
      });
      return () => {
        window.cancelAnimationFrame(frame);
      };
    }

    terminalOpenByThreadRef.current[activeThreadKey] = current;
  }, [activeThreadKey, focusComposer, terminalState.terminalOpen]);

  return {
    terminalFocusRequestId,
    terminalLaunchContext,
    activeTerminalLaunchContext,
    setTerminalOpen,
    toggleTerminalVisibility,
    splitTerminal,
    createNewTerminal,
    closeTerminal,
    runProjectScript,
  };
}
